import React, { useContext, useState, useEffect } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Grid, Box, Button, LinearProgress, IconButton, Tooltip,
} from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import CallIcon from "@material-ui/icons/Call";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import SendIcon from "@material-ui/icons/Send";
import MessageIcon from "@material-ui/icons/Message";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import TimerIcon from "@material-ui/icons/Timer";
import PeopleIcon from "@material-ui/icons/People";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import StarIcon from "@material-ui/icons/Star";
import ThumbUpIcon from "@material-ui/icons/ThumbUp";
import RefreshIcon from "@material-ui/icons/Refresh";
import * as XLSX from "xlsx";

import { toast } from "react-toastify";
import { isArray, isEmpty } from "lodash";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";
import Filters from "./Filters";
import { ChartsDate } from "./ChartsDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStyles,
    backgroundColor: "transparent !important",
    border: "none",
  },
  container: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  sectionLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    paddingLeft: 4,
    "&:first-child": { marginTop: 0 },
  },
  statCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    transition: "box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
    },
  },
  statIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statValue: {
    fontWeight: 700,
    fontSize: "1.6rem",
    lineHeight: 1.1,
    color: theme.palette.text.primary,
  },
  statLabel: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  npsCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
  },
  npsLabel: {
    fontWeight: 700,
    fontSize: "0.82rem",
    marginBottom: 4,
  },
  npsValue: {
    fontWeight: 800,
    fontSize: "1.4rem",
    marginBottom: 6,
  },
  progressBar: {
    borderRadius: 6,
    height: 8,
  },
  chartPaper: {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  attendantsPaper: {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  ratingBar: {
    borderRadius: 8,
    height: 12,
  },
  ratingPercent: {
    fontWeight: 800,
    fontSize: "1.6rem",
    color: "#F79009",
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  filterPaper: {
    borderRadius: 12,
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
}));

const STAT_COLORS = {
  teal:   { bg: "rgba(1,187,172,0.12)",  icon: "#01BBAC" },
  slate:  { bg: "rgba(71,96,110,0.12)",  icon: "#47606e" },
  purple: { bg: "rgba(88,82,171,0.12)",  icon: "#5852ab" },
  amber:  { bg: "rgba(247,144,9,0.12)",  icon: "#F79009" },
  red:    { bg: "rgba(238,69,18,0.12)",  icon: "#EE4512" },
  green:  { bg: "rgba(40,192,55,0.12)",  icon: "#28C037" },
  brown:  { bg: "rgba(128,87,83,0.12)",  icon: "#805753" },
  gold:   { bg: "rgba(140,107,25,0.12)", icon: "#8c6b19" },
  dark:   { bg: "rgba(51,49,51,0.10)",   icon: "#333133" },
  wine:   { bg: "rgba(138,44,64,0.12)",  icon: "#8a2c40" },
  blue:   { bg: "rgba(91,45,142,0.12)",  icon: "#5B2D8E" },
};

const Dashboard = () => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [fetchDataFilter, setFetchDataFilter] = useState(false);

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  let newDate = new Date();
  let nowIni = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, "0")}-01`;
  let now = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, "0")}-${String(newDate.getDate()).padStart(2, "0")}`;

  const [dateStartTicket, setDateStartTicket] = useState(nowIni);
  const [dateEndTicket, setDateEndTicket] = useState(now);
  const [queueTicket, setQueueTicket] = useState(false);
  const [period] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const t = setTimeout(async () => { if (isMounted) await fetchData(); }, 500);
    return () => { isMounted = false; clearTimeout(t); };
  }, [fetchDataFilter]);

  async function fetchData() {
    setLoading(true);
    let params = {};
    if (period > 0) {
      params = { days: period };
    } else {
      if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid())
        params.date_from = moment(dateStartTicket).format("YYYY-MM-DD");
      if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid())
        params.date_to = moment(dateEndTicket).format("YYYY-MM-DD");
    }
    if (Object.keys(params).length === 0) params = { days: 30 };

    try {
      const data = await find(params);
      const safeCounters = data.counters || {};
      setCounters(safeCounters);
      setAttendants(isArray(data.attendants) ? data.attendants : []);
    } catch (error) {
      toast.error("Error al cargar datos del dashboard");
      setCounters({});
      setAttendants([]);
    } finally {
      setLoading(false);
    }
  }

  function formatTime(minutes) {
    return moment().startOf("day").add(minutes, "minutes").format("HH[h] mm[m]");
  }

  const GetUsers = () => {
    return attendants.filter((u) => u.online === true).length;
  };

  const GetContacts = (all) => {
    const props = all ? {} : { dateStart: dateStartTicket, dateEnd: dateEndTicket };
    const { count } = useContacts(props);
    return count;
  };

  const GetMessages = (all, fromMe) => {
    let props = {};
    if (all) { props = { fromMe }; }
    else { props = { fromMe, dateStart: dateStartTicket, dateEnd: dateEndTicket }; }
    const { count } = useMessages(props);
    return count;
  };

  const StatCard = ({ icon, value, label, color = "blue" }) => {
    const c = STAT_COLORS[color] || STAT_COLORS.blue;
    return (
      <div className={classes.statCard}>
        <div className={classes.statIconWrap} style={{ background: c.bg }}>
          {React.cloneElement(icon, { style: { color: c.icon, fontSize: 24 } })}
        </div>
        <div>
          <div className={classes.statValue}>{value}</div>
          <div className={classes.statLabel}>{label}</div>
        </div>
      </div>
    );
  };

  const NpsCard = ({ label, value, barColor, textColor }) => (
    <div className={classes.npsCard}>
      <div className={classes.npsLabel} style={{ color: textColor }}>{label}</div>
      <div className={classes.npsValue} style={{ color: textColor }}>{value}%</div>
      <LinearProgress
        variant="determinate"
        value={Math.min(value, 100)}
        className={classes.progressBar}
        style={{ backgroundColor: `${barColor}30` }}
        classes={{ bar: undefined }}
      />
    </div>
  );

  const mainStats = [
    { label: "En atención",          value: counters.supportHappening || 0,                                        icon: <CallIcon />,             color: "teal"   },
    { label: "Esperando",            value: counters.supportPending || 0,                                          icon: <HourglassEmptyIcon />,   color: "slate"  },
    { label: "Finalizados",          value: counters.supportFinished || 0,                                         icon: <CheckCircleIcon />,      color: "purple" },
    { label: "Grupos",               value: counters.supportGroups || 0,                                           icon: <PeopleIcon />,           color: "teal"   },
    { label: "Agentes activos",      value: `${GetUsers()}/${attendants.length}`,                                  icon: <RecordVoiceOverIcon />,  color: "brown"  },
    { label: "Nuevos contactos",     value: counters.leads || 0,                                                   icon: <GroupAddIcon />,         color: "gold"   },
    { label: "Mensajes recibidos",   value: `${GetMessages(false, false)}/${GetMessages(true, false)}`,            icon: <MessageIcon />,          color: "dark"   },
    { label: "Mensajes enviados",    value: `${GetMessages(false, true)}/${GetMessages(true, true)}`,              icon: <SendIcon />,             color: "green"  },
    { label: "T.M. de atención",     value: formatTime(counters.avgSupportTime),                                   icon: <AccessAlarmIcon />,      color: "amber"  },
    { label: "T.M. de espera",       value: formatTime(counters.avgWaitTime),                                      icon: <TimerIcon />,            color: "wine"   },
    { label: "Tickets activos",      value: counters.activeTickets || 0,                                           icon: <ArrowUpwardIcon />,      color: "red"    },
    { label: "Tickets pasivos",      value: counters.passiveTickets || 0,                                          icon: <ArrowDownwardIcon />,    color: "green"  },
  ];

  const ticketStats = [
    { label: "Total atenciones",              value: counters.tickets || 0,        icon: <CallIcon />,             color: "teal"   },
    { label: "Esperando evaluación",          value: counters.waitRating || 0,     icon: <HourglassEmptyIcon />,   color: "slate"  },
    { label: "Sin evaluación",                value: counters.withoutRating || 0,  icon: <ThumbUpIcon />,          color: "wine"   },
    { label: "Evaluados",                     value: counters.withRating || 0,     icon: <StarIcon />,             color: "brown"  },
  ];

  const npsStats = [
    { label: "Score NPS",      value: counters.npsScore || 0,          barColor: "#5B2D8E", textColor: "#5B2D8E" },
    { label: "Promotores",     value: counters.npsPromotersPerc || 0,  barColor: "#22c55e", textColor: "#166534" },
    { label: "Neutros",        value: counters.npsPassivePerc || 0,    barColor: "#F79009", textColor: "#92400e" },
    { label: "Detractores",    value: counters.npsDetractorsPerc || 0, barColor: "#ef4444", textColor: "#991b1b" },
  ];

  return (
    <>
      {user.profile === "user" && user.showDashboard === "disabled" ? (
        <ForbiddenPage />
      ) : (
        <MainContainer>
          <Paper className={classes.mainPaper} variant="outlined">
            <div className={classes.container}>

              {/* Barra de filtros */}
              <div className={classes.filterBar}>
                <Tooltip title="Actualizar">
                  <IconButton size="small" onClick={() => setFetchDataFilter((p) => !p)} disabled={loading}>
                    <RefreshIcon fontSize="small" style={loading ? { animation: "spin 1s linear infinite" } : {}} />
                  </IconButton>
                </Tooltip>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setShowFilter((p) => !p)}
                  startIcon={showFilter ? <ClearIcon /> : <FilterListIcon />}
                >
                  {showFilter ? "Ocultar filtros" : "Filtros"}
                </Button>
              </div>

              {showFilter && (
                <div className={classes.filterPaper}>
                  <Filters
                    setDateStartTicket={setDateStartTicket}
                    setDateEndTicket={setDateEndTicket}
                    dateStartTicket={dateStartTicket}
                    dateEndTicket={dateEndTicket}
                    setQueueTicket={setQueueTicket}
                    queueTicket={queueTicket}
                    fetchData={setFetchDataFilter}
                  />
                </div>
              )}

              {/* ── Indicadores principales ── */}
              <Typography className={classes.sectionLabel}>Indicadores</Typography>
              <Grid container spacing={isMobile ? 1 : 2}>
                {mainStats.map((s, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <StatCard {...s} />
                  </Grid>
                ))}
              </Grid>

              {/* ── Atenciones ── */}
              <Typography className={classes.sectionLabel}>Atenciones</Typography>
              <Grid container spacing={isMobile ? 1 : 2}>
                {ticketStats.map((s, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <StatCard {...s} />
                  </Grid>
                ))}
              </Grid>

              {/* Índice de evaluación */}
              <Box mt={2} p={2} style={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 12,
              }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                    Índice de evaluación
                  </Typography>
                  <Typography className={classes.ratingPercent}>
                    {Number((counters.percRating || 0) / 100).toLocaleString(undefined, { style: "percent" })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(counters.percRating || 0, 100)}
                  className={classes.ratingBar}
                  style={{ backgroundColor: theme.palette.divider }}
                />
              </Box>

              {/* ── NPS ── */}
              <Typography className={classes.sectionLabel}>Satisfacción (NPS)</Typography>
              <Grid container spacing={isMobile ? 1 : 2}>
                {npsStats.map((s, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <NpsCard {...s} />
                  </Grid>
                ))}
              </Grid>

              {/* ── Agentes ── */}
              <Typography className={classes.sectionLabel}>Agentes</Typography>
              <div className={classes.attendantsPaper}>
                <TableAttendantsStatus attendants={attendants} loading={loading} />
              </div>

              {/* ── Gráficos ── */}
              <Typography className={classes.sectionLabel}>Gráficos</Typography>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} md={6}>
                  <div className={classes.chartPaper}>
                    <ChatsUser />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className={classes.chartPaper}>
                    <ChartsDate />
                  </div>
                </Grid>
              </Grid>

            </div>
          </Paper>
        </MainContainer>
      )}
    </>
  );
};

export default Dashboard;
